import { AxiosRequestConfig } from 'axios'

// Only mutating methods need an idempotency key. GET (and other safe methods)
// are naturally idempotent and must not carry the header.
export const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])

export const isMutatingMethod = (method?: string): boolean =>
  MUTATING_METHODS.has((method || 'get').toLowerCase())

export const generateIdempotencyKey = (): string => {
  const cryptoRef = typeof crypto !== 'undefined'
    ? (crypto as Crypto & { randomUUID?: () => string })
    : undefined

  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID()
  }

  if (cryptoRef?.getRandomValues) {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`
}

const safeStringify = (value: unknown): string => {
  if (value === undefined || value === null) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// Scope the key by method + url + body so that distinct operations (e.g.
// printing item A vs item B on the same endpoint) never share a key, while
// identical repeated requests do. NOTE: query params (config.params) are
// intentionally omitted because no mutating request in this app uses them —
// extend this if that ever changes.
export const buildRequestSignature = (config: AxiosRequestConfig): string => {
  const method = (config.method || 'get').toLowerCase()
  const url = config.url || ''
  return `${method} ${url} ${safeStringify(config.data)}`
}

interface IdempotencyEntry {
  key: string
  expiresAt: number
}

export interface IdempotencyKeyRegistryOptions {
  ttlMs?: number
  now?: () => number
  generateKey?: () => string
}

// Default lifetime of a key while unused. It is refreshed (sliding window) on
// every acquire and on every failure, so an operation that is actively being
// retried keeps its key, while an abandoned/cancelled one expires and is pruned
// (this is what stops a cancelled request from pinning a key forever).
const DEFAULT_TTL_MS = 60_000

export class IdempotencyKeyRegistry {
  private entries = new Map<string, IdempotencyEntry>()
  private readonly ttlMs: number
  private readonly now: () => number
  private readonly generateKey: () => string

  constructor(options: IdempotencyKeyRegistryOptions = {}) {
    this.ttlMs = options.ttlMs ?? DEFAULT_TTL_MS
    this.now = options.now ?? (() => Date.now())
    this.generateKey = options.generateKey ?? generateIdempotencyKey
  }

  // Returns the key to send. Concurrent/duplicate requests for the same
  // signature (and retries after a failure) get the same key; a fresh signature
  // or one whose key has expired gets a new key.
  acquire(signature: string): string {
    this.prune()

    const existing = this.entries.get(signature)
    if (existing) {
      existing.expiresAt = this.now() + this.ttlMs
      return existing.key
    }

    const key = this.generateKey()
    this.entries.set(signature, { key, expiresAt: this.now() + this.ttlMs })
    return key
  }

  // Success consumes the key: the next request for this signature is a new
  // operation and gets a new key.
  settleSuccess(signature?: string): void {
    if (signature) {
      this.entries.delete(signature)
    }
  }

  // Failure keeps the key (sliding its expiry) so a retry of the same operation
  // reuses it, letting the server dedupe a request that may already have been
  // applied before the error reached us.
  settleFailure(signature?: string): void {
    if (!signature) {
      return
    }
    const existing = this.entries.get(signature)
    if (existing) {
      existing.expiresAt = this.now() + this.ttlMs
    }
  }

  private prune(): void {
    const current = this.now()
    this.entries.forEach((entry, signature) => {
      if (entry.expiresAt <= current) {
        this.entries.delete(signature)
      }
    })
  }
}
